import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Toast,
  Tag,
  FloatingBubble,
  Grid,
  PullToRefresh,
} from 'antd-mobile';
import { AddOutline } from 'antd-mobile-icons';
import { brandService } from '../services/brandService';
import { Brand } from '../types';
import SearchHeader from '../components/SearchHeader';
import BottomNav from '../components/BottomNav';
import EmptyState from '../components/EmptyState';
import BrandSkeleton from '../components/BrandSkeleton';
import LazyImage from '../components/LazyImage';
import './BrandList.css';

const BrandList: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const navigate = useNavigate();

  const loadBrands = async () => {
    try {
      setLoading(true);
      const data = await brandService.getBrands();
      setBrands(data);
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: '加载品牌失败',
      });
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, []);

  const handleBrandClick = useCallback((id: string) => {
    navigate(`/brands/${id}`);
  }, [navigate]);

  const handleAddBrand = useCallback(() => {
    navigate('/brands/new');
  }, [navigate]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  // 使用 useMemo 优化筛选逻辑
  const filteredBrands = useMemo(() => {
    if (!searchText) return brands;
    return brands.filter(brand => 
      brand.brand_name.toLowerCase().includes(searchText.toLowerCase()) ||
      brand.brand_code.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [brands, searchText]);

  return (
    <div className="brand-list-container">
      {/* 搜索栏 */}
      <SearchHeader 
        placeholder="搜索品牌名称或编码" 
        onChange={handleSearch}
        onSearch={handleSearch}
      />

      <PullToRefresh onRefresh={loadBrands}>
        <div className="brand-list-content">
          {loading ? (
            <BrandSkeleton count={6} />
          ) : filteredBrands.length === 0 ? (
            <EmptyState 
              icon="📦" 
              title="暂无品牌" 
              description={searchText ? '没有找到相关品牌' : '点击右下角按钮添加品牌'} 
            />
          ) : (
            <Grid columns={2} gap={12}>
              {filteredBrands.map((brand, index) => (
                <Grid.Item key={brand.id}>
                  <Card 
                    className="brand-card"
                    onClick={() => handleBrandClick(brand.id)}
                  >
                    <div className="brand-card-content">
                      {/* 品牌Logo */}
                      <div className="brand-logo-wrapper">
                        {brand.brand_logo ? (
                          <LazyImage
                            src={brand.brand_logo}
                            alt={brand.brand_name}
                            className="brand-logo"
                            width={90}
                            height={90}
                            quality={85}
                            priority={index < 4} // 前4个品牌优先加载
                          />
                        ) : (
                          <div className="brand-logo-placeholder">
                            {brand.brand_name.substring(0, 2)}
                          </div>
                        )}
                      </div>

                      {/* 品牌信息 */}
                      <div className="brand-info">
                        <div className="brand-name">{brand.brand_name}</div>
                        <div className="brand-code">编码: {brand.brand_code}</div>
                        <div className="brand-status">
                          <Tag
                            color={brand.status === 'active' ? 'success' : 'default'}
                            fill="outline"
                            style={{ fontSize: '11px' }}
                          >
                            {brand.status === 'active' ? '启用中' : '已停用'}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  </Card>
                </Grid.Item>
              ))}
            </Grid>
          )}
        </div>
      </PullToRefresh>

      {/* 底部导航 */}
      <BottomNav />

      {/* 添加按钮 */}
      <FloatingBubble
        style={{
          '--initial-position-bottom': '80px',
          '--initial-position-right': '24px',
          '--edge-distance': '24px',
          '--background': '#ff6700',
        }}
        onClick={handleAddBrand}
      >
        <AddOutline fontSize={32} />
      </FloatingBubble>
    </div>
  );
};

export default BrandList;
